import { Component, ViewChild, OnInit, OnDestroy, Input, OnChanges } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';

import { SideMenuDosService } from '@app/services/side-menu-dos.service';
import { AuthService } from '@app/auth/services/auth.service';
import { MenuContent } from '@app/models/menu-content';
import { User } from '@app/models/user-data';
import { Routes } from '@app/constants/routes.constants';
import { AbstractSubscriptionManager } from '@app/shared/classes/subscription-manager';
import { ILink } from '@app/models/links';
import { SuppportLink } from '@app/constants/support-links.constants';
import { CORRECT_MONTH_NUMBER } from '@app/shared/constants/timesheet.constants';

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss']
})
export class SideMenuComponent extends AbstractSubscriptionManager implements OnInit, OnChanges, OnDestroy {
  @ViewChild('sidenav', { static: true })
  public sidenav: MatSidenav;
  public isAdmin = this._authService.isAdmin();
  private readonly _currentYear = new Date().getFullYear();
  private readonly _currentMonth = new Date().getMonth() + CORRECT_MONTH_NUMBER;
  private readonly _monthlyReportYear = this._setStartDate(new Date()).getFullYear();
  private readonly _monthlyReportMonth = this._setStartDate(new Date()).getMonth() + CORRECT_MONTH_NUMBER;

  @Input() user: User;
  @Input() emailFromToken: string;
  @Input() links: ILink[];

  adminMenuContent: MenuContent[] = [
    { iconName: 'all-timesheets', text: 'All Timesheets', route: Routes.Timesheets },
    {
      iconName: 'monthly-reports',
      text: 'Monthly Reports',
      route: `${Routes.MonthlyReport}/${this._monthlyReportYear}/${this._monthlyReportMonth}`
    },
    { iconName: 'entitlements', text: 'User Entitlements', route: Routes.Employees },
    { iconName: 'archive', text: 'Reports Archive', route: Routes.MonthlyArchive },
    { iconName: 'settings', text: 'Settings', route: Routes.Settings }
  ];

  defaultMenuContent: MenuContent[] = [
    { iconName: 'report-issue', text: 'Report Issue', route: '' },
    { iconName: 'tool-support', text: 'Tool Support', route: '' }
  ];

  userMenuRoutesContent: MenuContent[] = [
    {
      iconName: 'my-timesheets',
      text: 'My Timesheets',
      route: `${Routes.Timesheet}/${this._currentYear}/${this._currentMonth}`
    }
  ];

  userMenuLinksContent: MenuContent[] = [
    { iconName: 'instruction', text: 'IP Rights Policy', route: '' },
    ...this.defaultMenuContent
  ];

  constructor(public sideMenuService: SideMenuDosService, private _authService: AuthService, private _router: Router) {
    super();
  }

  ngOnInit(): void {
    this.subscription.add(this.sideMenuService.sideMenuAction$.subscribe(() => this.sidenav.toggle()));
  }

  ngOnChanges(): void {
    if (this.links) {
      this.defaultMenuContent = [
        { iconName: 'report-issue', text: 'Report Issue', route: this._getLinkByName(SuppportLink.ReportIssue) },
        { iconName: 'tool-support', text: 'Tool Support', route: this._getLinkByName(SuppportLink.ToolSupport) }
      ];

      this.userMenuLinksContent = [
        { iconName: 'instruction', text: 'IP Rights Policy', route: this._getLinkByName(SuppportLink.Instructions) },
        ...this.defaultMenuContent
      ];
    }
  }

  onLogout(): void {
    this._authService.signOut();
  }

  isRouteActive(route: Routes | string): boolean {
    // changing base route to /timesheets in case of viewing user timesheet in view mode
    const activeRoute =
      this._router.routerState.snapshot.url.split('/').length > 4
        ? Routes.Timesheets
        : this._router.routerState.snapshot.url;

    return route.split('/')[1] === activeRoute.split('/')[1];
  }

  private _setStartDate(startDate: Date): Date {
    if (startDate.getDate() <= 10) {
      startDate.setDate(1);
      startDate.setMonth(startDate.getMonth() - 1);
    }

    return startDate;
  }

  private _getLinkByName(linkName: SuppportLink): string {
    return this.links.find((link) => link.key === linkName).value;
  }
}
